/* choice_options.control_adapter.js */

var $ = require("jquery");
var _ = require("lodash");
var underscoreString = require("underscore.string");
_.mixin(underscoreString.exports());
var EventEmitter = require("events").EventEmitter;
var util = require("util");
var q = require("q");
const StyleUtil = require("../StyleUtil");

/**
 * @event ChoiceOptionsControlAdapter#ValueChanged
 * @type {Object}
 * @property oldValue {string} The previous value
 * @property newValue {string} The new value
 */

/**
 * @class ChoiceOptionsControlAdapter
 * @classdesc renders the Select Options Jtype.
 * @implements AngularControlAdapter
 *
 * @param logger {Bunyan} The logger
 * @param containingElem {DomElement} The containing dom element for this control
 * @param formCtrlDef {FormControlDefinition} The definition of this control object.
 *
 * @requires util
 * @requires EventEmitter
 *
 * @fires ChoiceOptionsControlAdapter#ValueChanged
 */
module.exports = function ChoiceOptionsAdapter(logger, containingElem, formCtrlDef) {
    var self = this;
    var loadDeferred = q.defer();
    //call Event Emitter Constructor
    EventEmitter.call(this);
    this._multiEditMode = _.get(formCtrlDef, "displayModeOptions.multiEditMode", false);
    var _scope;
    var _oldValue;
    let _oldInvalidStatus;
    let _oldInProgressStatus = false;
    var _options = {};
    this._accordionDisplay = _.get(formCtrlDef, "configSettings.Display.accordion", false);

    /********************************************************************************/
    /*** START Type Definitions                                                   ***/
    /********************************************************************************/

    /********************************************************************************/
    /*** END Type Definitions                                                     ***/
    /*** START Private API                                                        ***/
    /********************************************************************************/

    /**
     * The Choice has changed
     * @param choice {string} choice to switch the choice to.
     * @param configData {object} The configData associated with this choice.
     * @private
     */
    function _choiceChanged(choice, configData) {
        if (_options.hasOwnProperty(choice) || _.isUndefined(choice)) {
            var cs = _options[choice];
            var ri = formCtrlDef.refInstances;
            var cd = configData;

            //Handle the deselection.
            if (_.isUndefined(choice)) {
                cs = {};
                cd = {};
                ri = {};
            }

            _scope.FormRendererApi.render(
                cs,
                {
                    configurationData: cd,
                    refInstances: ri,
                    refTypes: formCtrlDef.refTypes,
                },
                true
            );
        } else {
            logger.warn(
                {choice: choice},
                "(ChoiceOptions._changeChoice) Cannot change the choice to one that does not exist."
            );
            if (!loadDeferred.isFulfilled()) {
                loadDeferred.resolve();
            }
        }
    }

    /**
     * Fire the Value Changed Event
     * @private
     */
    function _fireValueChanged() {
        //Get the new Value
        var newVal = self.getValue();
        self.emit("ValueChanged", {
            name: formCtrlDef.name,
            containingElem: containingElem,
            controlAdapter: self,
            formControlDefinition: formCtrlDef,
            oldValue: _oldValue,
            newValue: newVal,
        });
        _oldValue = newVal;
    }

    /**
     * Fire Invalid event
     * @private
     */
    function _fireInvalid() {
        let newVal = self.isInvalid();
        self.emit("Invalid", {
            name: formCtrlDef.name,
            containingElem: containingElem,
            controlAdapter: self,
            formControlDefinition: formCtrlDef,
            oldValue: _oldInvalidStatus,
            newValue: newVal,
        });
        _oldInvalidStatus = newVal;
    }

    function _fireInProgressStatusChanged() {
        let newInProgressStatus = self.isInProgress();
        self.emit("InProgress", {
            name: formCtrlDef.name,
            containingElem: containingElem,
            controlAdapter: self,
            formControlDefinition: formCtrlDef,
            oldInProgressStatus: _oldInProgressStatus,
            newInProgressStatus: newInProgressStatus,
        });
        _oldInProgressStatus = newInProgressStatus;
    }

    /********************************************************************************/
    /*** END Private API                                                          ***/
    /*** START Public API                                                         ***/
    /********************************************************************************/
    //The name of the control
    this.name = formCtrlDef.name;

    /**
     * @returns the data associated with this control.
     */
    this.getValue = function () {
        var val = formCtrlDef.value;
        if (_scope.FormRendererApi) {
            val = {};
            var choice = _scope.ChoiceOptions.selection.api.getValue();
            var insideVal = _scope.FormRendererApi.getValue();
            var insideValCs = _scope.FormRendererApi.getConfigurationSettings();

            if (insideVal.hasOwnProperty("@VALUE")) {
                insideVal = insideVal["@VALUE"];
            }

            //if this is a directly nested choice options
            if (insideValCs.hasOwnProperty("Choice")) {
                val = insideVal;
            } else if (choice) {
                val[choice] = insideVal;
            }
        }
        return val;
    };

    /**
     * Set this controls current value.
     * @param value {string} The value to set this control to.
     * @returns the value associated with this control, it might not always be the same as the value passed in for
     * example adapters with a ValueScope.PARENT.
     */
    this.setValue = function (value) {
        var cdProperties = Object.keys(value);
        var choices = [];

        function findChoiceViaProps(options) {
            var discovered;
            Object.keys(options).every(function (choice) {
                var choiceCS = options[choice];
                //recursively look through nested choices to find a match...
                if (choiceCS.hasOwnProperty("Choice")) {
                    var nestedOpts = _.omit(choiceCS.Choice.options, "@propertyOrder");
                    discovered = findChoiceViaProps(nestedOpts);
                    if (discovered) {
                        choices.unshift(choice);
                    }
                } else {
                    if (cdProperties.indexOf(choice) !== -1) {
                        discovered = choice;
                        choices.unshift(choice);
                    }
                }
                //Abort if we find what we are looking for
                if (discovered) {
                    return false;
                } else {
                    return true;
                }
            });
            return discovered;
        }

        findChoiceViaProps(_options);

        //Set the choice
        _scope.ChoiceOptions.selection.api.setValue(choices[0]);
        //render choice
        if (choices.length === 1) {
            //This is the last choice in the chain
            _choiceChanged(choices[0], value[choices[0]]);
        } else {
            _choiceChanged(choices[0], value);
        }
        //update oldValue
        _oldValue = self.getValue();

        return self.getValue();
    };

    /**
     * Return true if the component is multi edit active.
     * @returns {boolean}
     */
    this.getMultiEditActive = function () {
        return this._multiEditActive;
    };

    this.setMultiEditActive = function (flag) {
        if (!this._readOnlyField) {
            this.setReadOnly(!flag);
            let oldValue = this._multiEditActive;
            this._multiEditActive = flag;
            _scope.FormRendererApi.setUnderlingsMultiEditActive(flag);
            if (oldValue !== this._multiEditActive) {
                this.emit("MultiEditActive", {
                    oldValue: oldValue,
                    newValue: this._multiEditActive,
                });
            }
        }
    };

    this.isInvalid = function () {
        let invalid = false;
        if (_scope.FormRendererApi) {
            invalid = _scope.FormRendererApi.isInvalid();
        }
        return invalid;
    };

    this.isInProgress = function () {
        return _scope.FormRendererApi && _scope.FormRendererApi.isInProgress();
    };

    this.getValidationMessages = function () {
        let validationMessages = {};
        if (_scope.FormRendererApi) {
            let choice = _scope.ChoiceOptions.selection.api.getValue();
            let insideValidationMessages = _scope.FormRendererApi.getValidationMessages();
            let insideValConfigSettings = _scope.FormRendererApi.getConfigurationSettings();

            if (!_.isEmpty(insideValidationMessages)) {
                if (insideValidationMessages.hasOwnProperty("@VALUE")) {
                    insideValidationMessages = insideValidationMessages["@VALUE"];
                }
                if (insideValConfigSettings.hasOwnProperty("Choice")) {
                    validationMessages = insideValidationMessages;
                } else {
                    validationMessages[choice] = insideValidationMessages;
                }
            }
        }
        return validationMessages;
    };

    /**
     * Get the path properties that are not currently selected.
     */
    this.getNonSelectedPaths = function (path) {
        let allPossibleProps = Object.keys(_options);
        let currentProps = [_scope.ChoiceOptions.selection.api.getValue()];
        let nonSelectedProps = _.difference(allPossibleProps, currentProps);
        let pathExtension = "";
        if (!_.isEmpty(path)) {
            pathExtension = path + ".";
        }
        let nonSelectedPaths = [];
        nonSelectedProps.forEach((prop) => {
            nonSelectedPaths.push(pathExtension + prop);
        });
        return nonSelectedPaths;
    };

    this.toggleAccordion = () => {
        let collapsed = this.choiceElem[0].classList.contains("collapsed");
        if (collapsed) {
            this.choiceElem[0].classList.remove("collapsed");
        } else {
            this.choiceElem[0].classList.add("collapsed");
        }
    };

    /**
     * Tear down this control
     */
    this.tearDown = function () {
        if (this.accordionIcon) {
            this.accordionIcon.removeEventListener("click", this.toggleAccordion);
        }
    };

    /********************************************************************************/
    /*** END Public API                                                           ***/
    /*** Start Angular/Render Work                                                ***/
    /********************************************************************************/

    /**
     * A Hook to start the angular work, due to timing issues don't try anything angularis before this hook is called.
     * @param $scope {$scope} Angular $scope
     * @parma $templateCache {$templateCache}
     */
    this.$init = function ($scope, $templateCache) {
        _scope = $scope;
        $scope.formCtrlDef = formCtrlDef;
        var opts = [];
        formCtrlDef.configSettings.Choice.options["@propertyOrder"].forEach(function (option) {
            var optionCS = _.cloneDeep(formCtrlDef.configSettings.Choice.options[option]);
            _options[option] = optionCS;
            var label = option;
            if (_.has(optionCS, "Display.label")) {
                label = _.get(optionCS, "Display.label");
                // Clear the label because we don't want it
                // showing up a second time.
                delete optionCS.Display.label;
            }
            //This is a Boolean Choice selection
            if (optionCS.fieldType === "Boolean") {
                _options[option] = {
                    "@": "@",
                    Display: {
                        hidden: {
                            "@": "!",
                        },
                    },
                    value: optionCS.hasOwnProperty("value") ? optionCS.value : false,
                    fieldType: "Boolean",
                };
            }
            opts.push({label: label, value: option});
        });

        //Check if required field in validation object - if so, then do not allow user to select *none*
        var canDeselect = true;
        if (formCtrlDef.validationInfo && formCtrlDef.validationInfo.required) {
            canDeselect = false;
        }

        //Setup Settings Object
        $scope.ChoiceOptions = {
            selection: {
                api: {},
                settings: {
                    disableAutoSelect: true,
                    allowSingleDeselect: canDeselect,
                    options: opts,
                    onUserChange: function (newVal /*, OldVal */) {
                        _choiceChanged(newVal, {});
                    },
                },
            },
        };

        //Setup directive
        var template = $templateCache.get("templatecache/templates/choice_options.template.html");
        this.choiceElem = $(template);

        if (this._accordionDisplay) {
            let legend = this.choiceElem.find("label").first();
            this.accordionIcon = document.createElement("actionable-icon-gl");
            this.accordionIcon.classList.add("accordion_icon", "expanded");
            this.accordionIcon.dataset.dark = true;
            this.accordionIcon.dataset.icon = "form.expand_collapse";
            this.accordionIcon.addEventListener(
                "click",
                (this.toggleAccordion = this.toggleAccordion.bind(this))
            );
            legend.prepend(this.accordionIcon);
            if (_.get(self._accordionDisplay, "default") === "collapsed") {
                this.toggleAccordion();
            }
        }

        containingElem.append(this.choiceElem);

        let stylesConfig = _.get(formCtrlDef, "configSettings.Display.styles", {});
        StyleUtil.renderStylesConfig(stylesConfig, containingElem[0]);

        let choiceOptionsRendererElem = this.choiceElem.find(
            ".choice_options_contents > .choice_options_renderer"
        );
        StyleUtil.appendClassListToElement(
            _.get($scope, "jTypeContext.layout.container.cssClasses", []),
            choiceOptionsRendererElem[0]
        );

        let itemLayoutCssClass = _.get($scope, "jTypeContext.layout.item.cssClass", "");
        containingElem.addClass(itemLayoutCssClass);

        //Notify the world this adapter is ready to be compiled/linked and values retrieved
        self.emit("InitializationComplete", {
            initializedValue: self.getValue(),
        });
        return loadDeferred.promise;
    };
    this.$init.$inject = ["$scope", "$templateCache"];

    /**
     * A Hook for when this control has been compiled/Linked.
     * @param $scope {$scope} The Angular Scope variable
     *
     * if $compiledAndLinked.$inject is specified those injection parameters will be used as the arguments
     */
    this.$compiledAndLinked = function ($scope) {
        var configDataSet = false;
        var defaultVal;
        if (_.isString(formCtrlDef.value)) {
            if (!this._multiEditMode) {
                defaultVal = {};
                defaultVal[formCtrlDef.value] = {};
            }
        }
        //If this is an object with 1 property in it
        else if (
            _.isPlainObject(formCtrlDef.value) &&
            Object.keys(formCtrlDef.value).length === 2
        ) {
            if (!this._multiEditMode) {
                var optionName = Object.keys(formCtrlDef.value)[1];
                defaultVal = _options[optionName];
                _.merge(defaultVal, formCtrlDef.value[optionName]); //This will merge the value properties in
            }
        }

        if (!_.isEmpty(formCtrlDef.configData)) {
            defaultVal = _.defaults({}, formCtrlDef.configData, defaultVal);
        }
        if (defaultVal) {
            configDataSet = true;
            self.setValue(defaultVal);
        }

        if (_.get(formCtrlDef, "configSettings.Display.readOnly")) {
            this._readOnlyField = true;
        }

        //Hook up sub form renderer
        if (configDataSet) {
            $scope.FormRendererApi.once("AllControlsLoaded", (/*evt*/) => {
                if (this._readOnlyField) {
                    this.setReadOnly(true);
                }
                if (this._multiEditMode) {
                    this.setReadOnly(true);
                }
                loadDeferred.resolve();
            });
        } else {
            if (this._readOnlyField) {
                this.setReadOnly(true);
            }
            if (this._multiEditMode) {
                this.setReadOnly(true);
            }
            loadDeferred.resolve();
        }

        //Form Renderer Value changed.
        $scope.FormRendererApi.on("ValueChanged", function (/*evt*/) {
            _fireValueChanged();
        });

        //Form Renderer Invalid status changed.
        $scope.FormRendererApi.on("Invalid", function (/*evt*/) {
            _fireInvalid();
        });

        $scope.FormRendererApi.on("InProgress", function (/*evt*/) {
            _fireInProgressStatusChanged();
        });
    };
    this.$compiledAndLinked.$inject = ["$scope"];

    this.setReadOnly = function (flag) {
        if (flag) {
            _scope.ChoiceOptions.selection.api.disable();
        } else {
            _scope.ChoiceOptions.selection.api.enable();
        }
        _scope.FormRendererApi.setReadOnly(flag);
    };

    /********************************************************************************/
    /*** END Angular/Render Work                                                  ***/
    /********************************************************************************/
};
//Make this API inherit from EventEmitter
util.inherits(module.exports, EventEmitter);

//Tell the Form renderer that this is an Angular Control.
module.exports.isAngularControl = true;

//Tell the Form renderer that this adapter supports multi edit mode.
module.exports.isMultiEditSupported = true;
